import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import portalClient from './portalClient';
import PortalLayout from './PortalLayout';
import { Card, Skeleton, useToast } from '../design-system';
import './Portal.css';

const fmtDate = (d) =>
  (d ? new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

// What the client owes and what they've settled.
//
// Ordered the way the client reads it: what needs paying, then what we're still
// checking, then history. A settled invoice is not news; an unpaid proforma is.
function PortalBilling() {
  const toast = useToast();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    portalClient
      .get('/api/portal/invoices')
      .then((res) => setInvoices(res.data.invoices || []))
      .catch((err) => toast.danger(err.response?.data?.error || 'Could not load your invoices'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { payable, checking, settled } = useMemo(() => ({
    // Overdue first inside the payable group — the only ordering that matters.
    payable: invoices
      .filter((i) => !i.awaitingConfirmation && ['proforma_sent', 'issued', 'partly_paid'].includes(i.status))
      .sort((a, b) => (b.isOverdue ? 1 : 0) - (a.isOverdue ? 1 : 0)),
    checking: invoices.filter((i) => i.awaitingConfirmation),
    settled: invoices.filter((i) => ['paid', 'cancelled'].includes(i.status)),
  }), [invoices]);

  const totalDue = useMemo(
    () => payable.reduce((s, i) => s + (i.amountDue ?? i.total ?? 0), 0),
    [payable]
  );

  const Row = ({ inv, note }) => (
    <li key={inv.id}>
      <Link to={`/portal/billing/${inv.id}`} className="portal-list-row">
        <span className="portal-list-main">
          {inv.number || 'Invoice'}
          {inv.isProforma && <span className="portal-tag">Proforma</span>}
          <span className="portal-muted"> · {fmtDate(inv.date)}</span>
        </span>
        <span className="portal-bill-right">
          <span className="portal-bill-amount">{inr(inv.amountDue || inv.total)}</span>
          {note || (inv.isOverdue
            ? <span className="portal-overdue">Overdue · was due {fmtDate(inv.dueDate)}</span>
            : inv.dueDate ? <span className="portal-muted">Due {fmtDate(inv.dueDate)}</span> : null)}
        </span>
      </Link>
    </li>
  );

  const Section = ({ title, items, count, children }) => {
    if (!items.length) return null;
    return (
      <Card padding="md">
        <div className="portal-card-head">
          <span className="portal-card-title">{title}</span>
          <span className="portal-muted">{count ?? items.length}</span>
        </div>
        {children}
        <ul className="portal-list">{items}</ul>
      </Card>
    );
  };

  if (loading) {
    return <PortalLayout title="Billing"><Card><Skeleton.Text lines={5} /></Card></PortalLayout>;
  }

  if (!invoices.length) {
    return (
      <PortalLayout title="Billing" subtitle="Your invoices and receipts">
        <Card padding="lg">
          <div className="portal-empty">
            <span className="portal-empty-icon">🧾</span>
            <h3 className="portal-empty-title">Nothing to show yet</h3>
            <p className="portal-muted">Invoices from us will appear here as soon as they are raised.</p>
          </div>
        </Card>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout
      title="Billing"
      subtitle={totalDue > 0 ? `${inr(totalDue)} payable` : 'Nothing outstanding'}
    >
      <div className="portal-stack">
        <Section
          title="To pay"
          items={payable.map((i) => <Row key={i.id} inv={i} />)}
        />

        <Section
          title="We're checking these"
          items={checking.map((i) => (
            <Row key={i.id} inv={i} note={<span className="portal-muted">Payment reported — awaiting our confirmation</span>} />
          ))}
        >
          <p className="portal-muted" style={{ marginTop: 0 }}>
            You've told us about a payment on these. We'll confirm once we've matched it
            against our bank, and send you a receipt.
          </p>
        </Section>

        <Section
          title="Settled"
          items={settled.map((i) => (
            <Row
              key={i.id}
              inv={i}
              note={<span className="portal-muted">{i.status === 'cancelled' ? 'Cancelled' : 'Paid'}</span>}
            />
          ))}
        />
      </div>
    </PortalLayout>
  );
}

export default PortalBilling;
